import { Ionicons } from '@expo/vector-icons';
import { CalendarEvent, google, outlook } from 'calendar-link';
import moment from 'moment-timezone';
import { Box, Divider, Heading, HStack, Image, ScrollView, Stack, Text, VStack } from 'native-base';
import React, { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator } from 'react-native';
import { stripHtml } from 'string-strip-html';
import { Button, IconTitle, PageWrapper } from '../../components/ui';
import { CardAction } from '../../components/ui/cards/CardAction';
import { CardContainer } from '../../components/ui/cards/CardContainer';
import { useCampaign } from '../../hooks';
import { AppStackScreenWithNavProps } from '../../navigation/AppNavigation';
import { useAppSelector } from '../../store';
import { colors } from '../../styles';
import { openUrl } from '../../utils';

export const GraduwayEventScreen: React.FC<AppStackScreenWithNavProps<'GraduwayEvent'>> = ({ route }) => {
  const { campaign } = useCampaign();
  const { graduwayEvents } = useAppSelector((state) => state.graduwayEvents);

  const event = useMemo(() => {
    const eventId = route.params?.id?.replace('graduway-', '');
    return graduwayEvents.find((e) => e.id === eventId);
  }, [graduwayEvents, route.params?.id]);

  const timezone = useMemo(() => campaign?.timezone || 'UTC', [campaign]);

  const date = useMemo(() => {
    if (!event?.eventDate) return '';
    return moment(event.eventDate).tz(timezone).format('MMMM D, YYYY');
  }, [event, timezone]);

  const time = useMemo(() => {
    return event?.eventTime || '';
  }, [event]);

  const location = useMemo(() => {
    return stripHtml(event?.location || '').result.trim();
  }, [event]);

  const calendarEvent = useMemo((): CalendarEvent | null => {
    if (!event?.eventDate) return null;

    const start = moment(event.eventDate).tz(timezone).format();
    const end = moment(event.eventDate).tz(timezone).add(2, 'hours').format();

    return {
      title: event.title,
      description: event.description || '',
      location: event.location ?? 'Check event details for location',
      start,
      end,
      busy: true,
      url: event.eventUrl || 'http://www.rockthestreetwallstreet.com/',
    };
  }, [event, timezone]);

  const openGoogle = useCallback(() => {
    if (calendarEvent) {
      openUrl(google(calendarEvent));
    }
  }, [calendarEvent]);

  const openOutlook = useCallback(() => {
    if (calendarEvent) {
      openUrl(outlook(calendarEvent));
    }
  }, [calendarEvent]);

  const openEventUrl = useCallback(() => {
    if (event?.eventUrl) {
      openUrl(event.eventUrl);
    }
  }, [event]);

  const [imageLoadError, setImageLoadError] = useState(false);

  const showImage = useMemo(() => {
    return event?.imageUrl && !imageLoadError;
  }, [event?.imageUrl, imageLoadError]);

  if (!event) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%">
        <ActivityIndicator color={colors.darkGrey} />
      </Stack>
    );
  }

  return (
    <ScrollView>
      <PageWrapper>
        <Box p={4}>
          <Heading mb={4}>{event.title}</Heading>
          {showImage && (
            <Image
              source={{ uri: event.imageUrl }}
              alt={event.title}
              width="100%"
              height={200}
              resizeMode="contain"
              borderRadius={8}
              mb={4}
              bg={colors.lightGrey}
              onError={() => setImageLoadError(true)}
            />
          )}
          <CardContainer>
            <HStack alignItems="center" justifyContent="space-between">
              <Box w="50%">
                <IconTitle
                  title={date}
                  variant="info"
                  renderIcon={(color) => <Ionicons name="calendar-outline" color={color} size={20} />}
                />
              </Box>
              {time && (
                <Box w="50%">
                  <IconTitle
                    title={time}
                    variant="info"
                    renderIcon={(color) => <Ionicons name="time-outline" color={color} size={20} />}
                  />
                </Box>
              )}
            </HStack>
            <Divider mb={4} />
            {location && (
              <Heading size="xs" mb={4}>
                {location}
              </Heading>
            )}
            {event.description && <Text mb={4}>{event.description}</Text>}
            {calendarEvent && (
              <VStack space={2} mb={4}>
                <Button transparent onPress={openGoogle} text="Add to Google" color="secondary" />
                <Button transparent onPress={openOutlook} text="Add to Outlook" color="secondary" />
              </VStack>
            )}
            {event.eventUrl && (
              <>
                <Divider mb={4} />
                <CardAction actionText="View Event Details" actionColor="primary" onActionPress={openEventUrl} />
              </>
            )}
          </CardContainer>
        </Box>
      </PageWrapper>
    </ScrollView>
  );
};
